import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { WEB_MODAL_MAX_WIDTH } from '../../theme/webLayoutWidths';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  useWindowDimensions,
  Platform,
  Pressable,
} from 'react-native';

import { useSafeAreaInsets } from 'react-native-safe-area-context';
import FontAwesome from '@react-native-vector-icons/fontawesome/static';
import MaterialDesignIcons from '@react-native-vector-icons/material-design-icons/static';
import { ModalContext } from '../../Context/modalContext';
import moment from 'moment';
import Button from '../Reusables/Button';
import { useDispatch } from 'react-redux';
import { ClearCart, clearCartOrderData } from '../../redux/cartSlice';
import { ExtrasContext } from '../../Context/ExtrasContext';
import { PrintReceiptContext } from '../../Context/PrintReceiptContext';
import { BlurView } from '@react-native-community/blur';
import { setUpdatingCartitems } from '../../redux/cartSlice';
import {
  formatWithCommas,
  fs,
  iconSize,
  s,
  v,
} from '../../utils/libs';
import { shouldUseWebLayout } from '../../utils/layoutUtils';
import { isEmployeeAdminRole } from '../../utils/roles';
import ErrorContext from '../../Context/ErrorContext';
import ReviewService from '../../services/ReviewService';
import { GTFonts, theme } from '../../theme';
import { useAppSelector } from '../../redux/helper';
import { getDashboardTotals } from '../../api/backoffice';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import {
  setTodayDashboardData,
  setTodayDashboardError,
} from '../../redux/backoffice/dashboardSlice';
import { useIsFocused } from '@react-navigation/native';
import { useHeaderTooltip } from '../Reusables/headers/HeaderTooltipProvider';
import { selectTooltipsDisabled } from '../../redux/SettingsSlice';
import { formatDisplayDateTime } from '../../utils/formatDisplayDateTime';
import TestLocationBanner from '../Reusables/TestLocationBanner';
const PaymentModal = ({
  paymentInfo,
  navigation,
  orderID,
}: {
  paymentInfo: any;
  navigation: any;
  orderID: any;
}) => {
  const employeedata = useAppSelector(
    state => state.auth.inApp.account.employeeInfo,
  );
  const owner = useAppSelector(s => s.backofficeOwnerAuth);
  const axiosBackoffice = useBackofficeAxios();
  const { width, height } = useWindowDimensions();
  const isAdmin = isEmployeeAdminRole(employeedata?.bp_employee_role);
  const useConstrainedModal = shouldUseWebLayout(width, height);
  const modalInnerWidth = useMemo(() => {
    if (useConstrainedModal) {
      const outer = Math.min(width * 0.9, WEB_MODAL_MAX_WIDTH);
      // modalView has horizontal padding `s(8)` + buttons container has `s(16)` padding each side
      return Math.max(240, outer - s(16) - s(32));
    }
    return width * 0.8 - s(32);
  }, [width, useConstrainedModal]);
  const insets = useSafeAreaInsets();
  const { paymentModal, setPaymentModal } = useContext(ModalContext);
  const { btprint, shareReceipt } = useContext(PrintReceiptContext);
  const dispatch = useDispatch();
  const { handleError } = useContext(ErrorContext);
  const isFocused = useIsFocused();
  const shouldShowPaymentModal = paymentModal && isFocused;

  const openPrinterSettings = useCallback(() => {
    try {
      navigation.navigate('PrinterSettingsScreen');
    } catch (e) {
      console.log('openPrinterSettings error', e);
    }
  }, [navigation]);

  const printerBtnRef = useRef<any>(null);
  const tooltipApi = useHeaderTooltip();
  const tooltipsDisabled = useAppSelector(selectTooltipsDisabled);
  const printerTooltipId = useRef(
    `printer-settings-tooltip-${Math.random().toString(36).slice(2)}`,
  );

  const showPrinterTooltip = useCallback(() => {
    if (!tooltipApi || tooltipsDisabled) return;
    tooltipApi.showTooltip({
      id: printerTooltipId.current,
      title: 'Printer Settings',
      targetRef: printerBtnRef,
      placement: 'bottom',
    });
  }, [tooltipApi, tooltipsDisabled]);

  const hidePrinterTooltip = useCallback(
    (delayMs?: number) => {
      tooltipApi?.hideTooltip(printerTooltipId.current, delayMs);
    },
    [tooltipApi],
  );

  const fetchToday = async () => {
    if (!isAdmin || !owner?.accessToken) return;
    const ownerId = String((employeedata as any)?.bp_owner_id || '');
    if (!ownerId) return;
    try {
      const startDate = moment().startOf('day').toISOString();
      const endDate = moment().endOf('day').toISOString();
      const res = await getDashboardTotals(
        axiosBackoffice as any,
        ownerId,
        startDate,
        endDate,
        { accessToken: owner.accessToken },
        'today',
      );
      if (res?.status === 200) {
        dispatch(setTodayDashboardData(res.data.data));
      } else {
        dispatch(
          setTodayDashboardError(String(res?.data?.message || 'Failed')),
        );
      }
    } catch (e: any) {
      dispatch(
        setTodayDashboardError(e?.message || 'Error fetching dashboard'),
      );
    }
  };
  useEffect(() => {
    fetchToday();
    dispatch(setUpdatingCartitems(false));
    dispatch(ClearCart());
    dispatch(clearCartOrderData());
  }, []);

  const BackToShop = () => {
    setPaymentModal(false);
    //@ts-ignore
    navigation.popToTop();
    navigation.navigate('MainScreen');
    // Trigger review on next frame after UI is settled
    requestAnimationFrame(() => {
      ReviewService.onEligibleAction('SELL_COMPLETED');
    });
  };

  const canShareReceipt = useMemo(() => {
    if (Platform.OS === 'web') {
      return navigator.share !== undefined;
    }
    return true;
  }, []);

  if (
    !paymentInfo ||
    !paymentInfo?.payment ||
    !paymentInfo?.payment?.bp_payment_updated_at
  ) {
    return null;
  }
  return (
    <Modal
      animationType="slide"
      visible={shouldShowPaymentModal}
      transparent={true}
      statusBarTranslucent={true}
      navigationBarTranslucent={true}>
      <BlurView
        blurAmount={1}
        blurType="dark"
        style={[{ height: '100%', width: '100%' }]}>
        <View style={styles.centeredView}>
          <View
            style={[
              styles.modalView,
              useConstrainedModal && styles.modalViewConstrained,
            ]}>
            <Pressable
              ref={printerBtnRef}
              onPress={() => {
                tooltipApi?.hideActiveTooltip();
                openPrinterSettings();
              }}
              onHoverIn={Platform.OS === 'web' ? showPrinterTooltip : undefined}
              onHoverOut={
                Platform.OS === 'web' ? () => hidePrinterTooltip(10) : undefined
              }
              onBlur={
                Platform.OS === 'web' ? () => hidePrinterTooltip(0) : undefined
              }
              hitSlop={s(10)}
              accessibilityRole="button"
              accessibilityLabel="Printer settings"
              style={styles.printerSettingsBtn}>
              <MaterialDesignIcons
                name="printer-pos-cog"
                size={iconSize(28)}
                color={theme.colors.text}
              />
            </Pressable>
            <View style={{ marginBottom: v(10) }}>
              <FontAwesome
                name="check-circle"
                size={iconSize(60)}
                color={'#4BB543'}
              />
            </View>
            <View
              style={{
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: v(10),
              }}>
              <Text
                style={{
                  fontSize: fs(24),
                  color: '#4BB543',
                  marginBottom: v(10),
                }}>
                Transaction Successful
              </Text>
              <Text
                style={{
                  fontSize: fs(16),
                  lineHeight: fs(20),
                  fontFamily: GTFonts.regular,
                  marginVertical: v(10),
                }}>
                {formatDisplayDateTime(
                  paymentInfo?.payment?.bp_payment_updated_at,
                  '',
                )}
              </Text>
            </View>
            <TestLocationBanner
              variant="subtle"
              style={{ alignSelf: 'stretch', marginBottom: v(14) }}
            />
            <View>
              <View style={styles.section}>
                <Text style={styles.text}>Pay Method</Text>
                <Text style={styles.text2}>
                  {paymentInfo?.payment?.bp_payments &&
                  paymentInfo?.payment?.bp_payments?.length > 0
                    ? paymentInfo?.payment?.bp_payments[
                        paymentInfo?.payment?.bp_payments.length - 1
                      ].method
                    : paymentInfo?.payment?.bp_payment_method}
                </Text>
              </View>
              <View style={styles.section}>
                <Text style={styles.text}>Total Bill</Text>
                <Text style={styles.text2}>
                  KSh {formatWithCommas(paymentInfo?.payment.bp_payment_amount)}
                  .00
                </Text>
              </View>

              <View style={styles.section}>
                <Text style={styles.text}>Current Bill</Text>
                <Text style={styles.text2}>
                  KSh{'\u00A0'}
                  {formatWithCommas(
                    paymentInfo?.payment?.bp_current_bill_amount,
                  )}
                  .00
                </Text>
              </View>

              <View style={styles.section}>
                <Text style={styles.text}>Cash Received</Text>
                <Text style={styles.text2}>
                  KSh {formatWithCommas(paymentInfo?.payment.bp_amount_paid)}
                  .00
                </Text>
              </View>
              {paymentInfo?.payment.bp_payment_amount_due >= 0 ? (
                <View style={styles.section}>
                  <Text style={styles.text}>Balance Remaining</Text>
                  <Text style={styles.text2}>
                    KSh{'\u00A0'}
                    {formatWithCommas(
                      paymentInfo?.payment?.bp_payment_amount_due,
                    )}
                    .00
                  </Text>
                </View>
              ) : null}
              {(() => {
                const refunds = paymentInfo?.payment?.bp_payment_refunds;
                const payments = paymentInfo?.payment?.bp_payments;
                const latestPayment =
                  payments && payments.length > 0
                    ? payments[payments.length - 1]
                    : null;
                const latestPaymentId = latestPayment?._id;
                const latestRefund =
                  refunds && refunds.length > 0
                    ? refunds[refunds.length - 1]
                    : null;
                const latestRefundAmount = latestRefund?.amount;
                const refundPaymentId = latestRefund?.payment_id;

                // Rules:
                // - If refund has no payment_id, show last refund amount
                // - If refund has payment_id, only show it when it matches the last payment id
                // - Otherwise fallback to 0
                const shouldShowRefund =
                  latestRefundAmount !== null &&
                  latestRefundAmount !== undefined &&
                  latestRefundAmount >= 0 &&
                  (refundPaymentId == null ||
                    (latestPaymentId != null &&
                      String(refundPaymentId) === String(latestPaymentId)));
                const amountToShow = shouldShowRefund ? latestRefundAmount : 0;

                return amountToShow >= 0 ? (
                  <View style={styles.section}>
                    <Text style={styles.text}>Change</Text>
                    <Text style={styles.text2}>
                      KSh{'\u00A0'}
                      {formatWithCommas(amountToShow, 0, true)}
                      .00
                    </Text>
                  </View>
                ) : null;
              })()}
            </View>

            <View
              style={[styles.buttons, { marginBottom: v(10) + insets.bottom }]}>
              <Button
                text="Print Receipt"
                rippleColor={'#367588'}
                fontSize={fs(16)}
                width={modalInnerWidth}
                icon="print"
                height={v(46)}
                borderRadius={s(8)}
                paddingHorizontal={s(18)}
                onPress={() => btprint('print-tx')}
                backgroundColor={'#0088FE'}
              />
              <Button
                text={canShareReceipt ? 'Share Receipt' : 'Download Receipt'}
                rippleColor={'#4a4280'}
                fontSize={fs(16)}
                width={modalInnerWidth}
                icon={canShareReceipt ? 'share-nodes' : 'download'}
                height={v(46)}
                borderRadius={s(8)}
                paddingHorizontal={s(18)}
                onPress={() => shareReceipt('print-tx')}
                backgroundColor={'#6C63FF'}
              />
              <Button
                text="To Shop"
                rippleColor={'#367588'}
                width={modalInnerWidth}
                fontSize={fs(16)}
                icon={'shop'}
                height={v(46)}
                paddingHorizontal={s(18)}
                borderRadius={s(8)}
                backgroundColor={theme.colors.success}
                onPress={BackToShop}
              />
            </View>
          </View>
        </View>
      </BlurView>
    </Modal>
  );
};

export default PaymentModal;

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: v(22),
    width: '100%',
  },
  modalView: {
    margin: s(5),
    marginHorizontal: s(5),
    backgroundColor: 'white',
    borderRadius: s(8),
    padding: s(8),
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: s(4),
    elevation: 5,
  },
  modalViewConstrained: {
    // Use a fixed max width to avoid scaling surprises from normalizeSize.
    maxWidth: WEB_MODAL_MAX_WIDTH,
    width: '90%',
  },
  section: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: v(10),
  },
  text: {
    marginRight: s(30),
    fontSize: fs(18),
    lineHeight: fs(20),
    fontFamily: GTFonts.regular,
  },
  text2: {
    fontSize: fs(16),
    lineHeight: fs(18),
    fontFamily: GTFonts.bold,
    color: theme.colors.text,
  },
  buttons: {
    marginTop: v(20),
    gap: s(12),
    paddingHorizontal: s(16),
    flexDirection: 'column',
    alignItems: 'center',
  },
  printerSettingsBtn: {
    position: 'absolute',
    top: s(6),
    right: s(6),
    padding: s(8),
    borderRadius: s(24),
    backgroundColor: 'rgba(0,0,0,0.04)',
    zIndex: 10,
  },
});
